/**
 * DraggablePanel — generic wrapper для трёх плавающих панелей карты
 * (Crops / AI / Satellites). Тащится за header (handle = ⋮⋮ GripVertical).
 *
 * Affordance (AK-293): grip — labeled focusable <button> (title "Перетащить" +
 * aria-label "Перетащить панель"), курсор grab/grabbing на header и handle, так
 * что назначение перетаскивания читается с одного взгляда и доступно
 * screen-reader'ам. На mobile (disabled) grip не рендерится — drag отключён.
 *
 * Spec v1.3: позиции персистятся в useMapStore.panelPositions, на mobile
 * (<768px) — drag отключён, panels стекаются (это решается родителем,
 * у нас просто prop disabled).
 *
 * Implementation note: использует pointer events (cross-device), ограничивает
 * позицию рамками viewport map container.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { GripVertical, ChevronDown, ChevronRight } from 'lucide-react';
import { useMapStore, type PanelId } from '@/lib/map/useMapStore';
import { cn } from '@/lib/utils';

interface DraggablePanelProps {
  panelId: PanelId;
  title: ReactNode;
  /** When true (mobile), panel is rendered inline by parent; this is a no-op marker. */
  disabled?: boolean;
  className?: string;
  /** Optional content next to the title (right-aligned). */
  headerExtra?: ReactNode;
  /** Если true (default) — добавляется chevron-кнопка свернуть/развернуть. */
  collapsible?: boolean;
  /** Начальное состояние сворачивания. */
  defaultCollapsed?: boolean;
  children: ReactNode;
}

export function DraggablePanel({
  panelId,
  title,
  disabled = false,
  className,
  headerExtra,
  collapsible = true,
  defaultCollapsed = false,
  children,
}: DraggablePanelProps) {
  const pos = useMapStore((s) => s.panelPositions[panelId]);
  const setPanelPosition = useMapStore((s) => s.setPanelPosition);

  const ref = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const toggleCollapsed = useCallback((e: React.MouseEvent | React.PointerEvent) => {
    e.stopPropagation();
    setCollapsed((prev) => !prev);
  }, []);

  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      const next = {
        x: Math.max(0, dragRef.current.origX + dx),
        y: Math.max(0, dragRef.current.origY + dy),
      };
      // Constrain to parent (map container) rect.
      const parent = ref.current?.parentElement;
      const node = ref.current;
      if (parent && node) {
        const pBox = parent.getBoundingClientRect();
        const nBox = node.getBoundingClientRect();
        next.x = Math.min(next.x, pBox.width - nBox.width);
        next.y = Math.min(next.y, pBox.height - nBox.height);
      }
      setPanelPosition(panelId, next);
    },
    [panelId, setPanelPosition],
  );

  const onPointerUp = useCallback(() => {
    setDragging(false);
    dragRef.current = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', onPointerUp);
  }, [onPointerMove]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (disabled) return;
      e.preventDefault();
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origX: pos.x,
        origY: pos.y,
      };
      setDragging(true);
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
      window.addEventListener('pointercancel', onPointerUp);
    },
    [disabled, pos.x, pos.y, onPointerMove, onPointerUp],
  );

  // Cleanup if unmounted mid-drag.
  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, [onPointerMove, onPointerUp]);

  if (disabled) {
    // Mobile inline mode — no absolute positioning, just a styled card.
    return (
      <div
        ref={ref}
        className={cn(
          'rounded-xl bg-card/95 backdrop-blur border border-border shadow-sm',
          className,
        )}
      >
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border/60">
          <div className="text-sm font-semibold text-foreground">{title}</div>
          <div className="flex items-center gap-1">
            {headerExtra}
            {collapsible && (
              <button
                type="button"
                onClick={toggleCollapsed}
                className="rounded hover:bg-accent p-1 transition-colors"
                aria-label={collapsed ? 'Развернуть' : 'Свернуть'}
                title={collapsed ? 'Развернуть' : 'Свернуть'}
              >
                {collapsed ? (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )}
              </button>
            )}
          </div>
        </div>
        {!collapsed && <div>{children}</div>}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={cn(
        'absolute z-10 rounded-xl bg-card/95 backdrop-blur border border-border shadow-lg',
        'select-none',
        dragging && 'shadow-2xl',
        className,
      )}
      style={{ left: pos.x, top: pos.y }}
    >
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-2',
          !collapsed && 'border-b border-border/60',
          dragging ? 'cursor-grabbing' : 'cursor-grab',
        )}
        onPointerDown={onPointerDown}
      >
        {/* Drag handle — labeled, focusable affordance (AK-293). */}
        <button
          type="button"
          aria-label="Перетащить панель"
          title="Перетащить"
          data-testid={`panel-drag-handle-${panelId}`}
          // onPointerDown propagates to the header so dragging works whether
          // the user grabs the icon or the title bar.
          className={cn(
            'rounded p-1 -ml-1 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors shrink-0 touch-none',
            dragging ? 'cursor-grabbing' : 'cursor-grab',
          )}
        >
          <GripVertical className="h-4 w-4" aria-hidden />
        </button>
        <div className="text-sm font-semibold text-foreground flex-1">
          {title}
        </div>
        {headerExtra}
        {collapsible && (
          <button
            type="button"
            onClick={toggleCollapsed}
            onPointerDown={(e) => e.stopPropagation()}
            className="rounded hover:bg-accent p-1 transition-colors shrink-0"
            aria-label={collapsed ? 'Развернуть' : 'Свернуть'}
            title={collapsed ? 'Развернуть' : 'Свернуть'}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
        )}
      </div>
      {!collapsed && <div>{children}</div>}
    </div>
  );
}
